// PrimeReact InputNumber: onValueChange returns InputNumberValueChangeEvent
// with `e.value` typed as number | null.
// Verified from existing usages:
//   - apps/web/src/modules/ivr-builder/NodeParamsSidebar.tsx:83 — onValueChange={(e) => onUpdate(node.id, { maxDigits: e.value ?? 1 })}
//   - apps/web/src/pages/agent/DevicesPage.tsx:243 — onValueChange={(e) => setForm({ ...form, priority: e.value ?? 1 })}

import { useId, type JSX } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { InputNumber } from 'primereact/inputnumber'

interface Props {
  name: string
  label: string
  helpText?: string
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  required?: boolean
  useGrouping?: boolean
  /** Restricts input to whole numbers by disallowing decimal digits (no decimal point can be typed). */
  integerOnly?: boolean
}

export function FormNumber({ name, label, helpText, min, max, step, required, disabled, useGrouping, integerOnly }: Props): JSX.Element {
  const { control, formState: { errors } } = useFormContext()
  const error = errors[name]
  const errorMessage = typeof error?.message === 'string' ? error.message : undefined
  const id = useId()
  const errorId = `${id}-error`
  const helpId = `${id}-help`
  const describedBy = [helpText && helpId, errorMessage && errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className="flex flex-col gap-1 mb-3">
      <label htmlFor={id} className="text-sm font-medium">
        {label}{required && <span aria-hidden> *</span>}
      </label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <InputNumber
            inputId={id}
            value={field.value as number | null}
            onValueChange={(e) => field.onChange(e.value)}
            onBlur={field.onBlur}
            min={min}
            max={max}
            step={step}
            {...(integerOnly ? { minFractionDigits: 0, maxFractionDigits: 0 } : {})}
            disabled={disabled}
            useGrouping={useGrouping}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={describedBy}
          />
        )}
      />
      {helpText && <small id={helpId} className="text-gray-500">{helpText}</small>}
      {errorMessage && (
        <small id={errorId} role="alert" className="text-red-600">{errorMessage}</small>
      )}
    </div>
  )
}
